import React, { useEffect, useMemo, useRef, useState } from "react";
import * as pdfjsLib from "pdfjs-dist/build/pdf.mjs";
import { PDFDocument } from "pdf-lib";
import JSZip from "jszip";
import Dropzone from "./Dropzone";

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;

function baseName(filename = "document.pdf") {
  return filename.replace(/\.[^/.]+$/, "");
}

// "1,3-5,9" -> [1,3,4,5,9] (unique, sorted)
function parsePages(text, maxPages) {
  const raw = (text || "").replace(/\s+/g, "");
  if (!raw) return [];

  const set = new Set();

  raw.split(",").forEach((part) => {
    if (!part) return;

    if (part.includes("-")) {
      const [a, b] = part.split("-").map((x) => parseInt(x, 10));
      if (!Number.isFinite(a) || !Number.isFinite(b)) return;

      const start = Math.min(a, b);
      const end = Math.max(a, b);

      for (let n = start; n <= end; n++) {
        if (n >= 1 && n <= maxPages) set.add(n);
      }
    } else {
      const n = parseInt(part, 10);
      if (Number.isFinite(n) && n >= 1 && n <= maxPages) set.add(n);
    }
  });

  return Array.from(set).sort((x, y) => x - y);
}

// [1,3,4,5,9] -> "1,3-5,9"
function toRangeText(list) {
  if (!list?.length) return "";
  const arr = [...list].sort((a, b) => a - b);

  const parts = [];
  let start = arr[0];
  let prev = arr[0];

  for (let i = 1; i < arr.length; i++) {
    const n = arr[i];
    if (n === prev + 1) {
      prev = n;
      continue;
    }
    parts.push(start === prev ? `${start}` : `${start}-${prev}`);
    start = prev = n;
  }
  parts.push(start === prev ? `${start}` : `${start}-${prev}`);
  return parts.join(",");
}

export default function SplitPdf() {
  const [file, setFile] = useState(null);
  const [pageCount, setPageCount] = useState(null);

  // text input -> apply to selection
  const [rangeText, setRangeText] = useState("1");

  // selection set (1-based page numbers)
  const [selected, setSelected] = useState(() => new Set([1]));

  // preview thumbs
  const [thumbs, setThumbs] = useState([]); // [{ pageNum, url }]
  const thumbsRef = useRef([]);

  const [mode, setMode] = useState("single"); // single | multi
  const [loading, setLoading] = useState(false);
  const [thumbLoading, setThumbLoading] = useState(false);

  const [result, setResult] = useState(null); // { url, name }
  const [zipUrl, setZipUrl] = useState(null);

  const cleanup = () => {
    if (result?.url) URL.revokeObjectURL(result.url);
    if (zipUrl) URL.revokeObjectURL(zipUrl);
    setResult(null);
    setZipUrl(null);
  };

  const cleanupThumbs = () => {
    thumbsRef.current.forEach((u) => URL.revokeObjectURL(u));
    thumbsRef.current = [];
    setThumbs([]);
  };

  const resetAll = () => {
    cleanup();
    cleanupThumbs();
    setFile(null);
    setPageCount(null);
    setRangeText("1");
    setSelected(new Set([1]));
  };

  const onPick = (f) => {
    resetAll();
    if (!f) return;
    setFile(f);
  };

  // Detect pages + generate thumbnails
  useEffect(() => {
    let cancelled = false;

    async function init() {
      if (!file) return;

      setThumbLoading(true);

      try {
        const buf = await file.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: buf }).promise;

        if (cancelled) return;

        setPageCount(pdf.numPages);

        // reset selection to first page by default
        setSelected(new Set([1]));
        setRangeText("1");

        cleanupThumbs();
        const out = [];

        // thumbnails (scale small)
        for (let p = 1; p <= pdf.numPages; p++) {
          const page = await pdf.getPage(p);
          const viewport = page.getViewport({ scale: 0.28 });

          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d", { alpha: false });

          canvas.width = Math.floor(viewport.width);
          canvas.height = Math.floor(viewport.height);

          await page.render({ canvasContext: ctx, viewport }).promise;

          const blob = await new Promise((resolve, reject) => {
            canvas.toBlob(
              (b) =>
                b ? resolve(b) : reject(new Error("toBlob thumb échoué")),
              "image/jpeg",
              0.8
            );
          });

          if (cancelled) return;

          const url = URL.createObjectURL(blob);
          thumbsRef.current.push(url);
          out.push({ pageNum: p, url });
        }

        if (!cancelled) setThumbs(out);
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setPageCount(null);
          cleanupThumbs();
          alert("Impossible de lire ce PDF.");
        }
      } finally {
        if (!cancelled) setThumbLoading(false);
      }
    }

    init();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  useEffect(() => {
    return () => {
      cleanup();
      cleanupThumbs();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedList = useMemo(() => {
    return Array.from(selected).sort((a, b) => a - b);
  }, [selected]);

  const canRun = useMemo(() => {
    return (
      !!file &&
      !loading &&
      selectedList.length > 0 &&
      typeof pageCount === "number"
    );
  }, [file, loading, selectedList, pageCount]);

  const togglePage = (pageNum) => {
    cleanup();
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(pageNum)) next.delete(pageNum);
      else next.add(pageNum);
      return next;
    });
  };

  const applyRangeToSelection = () => {
    cleanup();
    if (typeof pageCount !== "number") return;

    const parsed = parsePages(rangeText, pageCount);
    if (!parsed.length) {
      alert("Aucune page valide trouvée. Exemple : 3 ou 2-6 ou 1,3-5,9");
      return;
    }
    setSelected(new Set(parsed));
  };

  const selectAll = () => {
    cleanup();
    if (typeof pageCount !== "number") return;
    const s = new Set();
    for (let i = 1; i <= pageCount; i++) s.add(i);
    setSelected(s);
  };

  const selectNone = () => {
    cleanup();
    setSelected(new Set());
  };

  const invertSelection = () => {
    cleanup();
    if (typeof pageCount !== "number") return;
    setSelected((prev) => {
      const next = new Set();
      for (let i = 1; i <= pageCount; i++) {
        if (!prev.has(i)) next.add(i);
      }
      return next;
    });
  };

  // keep rangeText in sync when user clicks pages (optionnel, mais super UX)
  useEffect(() => {
    if (!file || typeof pageCount !== "number") return;
    setRangeText(toRangeText(selectedList) || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedList.join("|"), file, pageCount]);

  const runSplit = async () => {
    if (!file) return;
    if (typeof pageCount !== "number") return;

    cleanup();
    setLoading(true);

    try {
      const selectedPages = selectedList;
      if (!selectedPages.length) {
        alert("Sélectionne au moins une page.");
        return;
      }

      const inputBytes = await file.arrayBuffer();
      const src = await PDFDocument.load(inputBytes);

      const idx = selectedPages.map((p) => p - 1); // 0-based

      if (mode === "single") {
        const out = await PDFDocument.create();
        const copied = await out.copyPages(src, idx);
        copied.forEach((pg) => out.addPage(pg));

        const bytes = await out.save();
        const blob = new Blob([bytes], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);

        const nice = toRangeText(selectedPages) || "pages";
        const name = `${baseName(file.name)}-extrait-${nice}.pdf`;
        setResult({ url, name });
      } else {
        const zip = new JSZip();
        const folder = zip.folder(`${baseName(file.name)}-extraits`) || zip;

        for (let i = 0; i < idx.length; i++) {
          const pageIndex = idx[i];
          const pageHuman = selectedPages[i];

          const out = await PDFDocument.create();
          const [pg] = await out.copyPages(src, [pageIndex]);
          out.addPage(pg);

          const bytes = await out.save();
          folder.file(`${baseName(file.name)}-page-${pageHuman}.pdf`, bytes);
        }

        const zipBlob = await zip.generateAsync({ type: "blob" });
        const url = URL.createObjectURL(zipBlob);
        setZipUrl(url);
      }
    } catch (e) {
      console.error(e);
      alert("Erreur pendant l’extraction des pages.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section style={{ padding: 16 }}>
      <h2>Extraire des pages d’un PDF</h2>
      <small>
        Prévisualise, clique pour sélectionner, puis télécharge (100% local).
      </small>

      <Dropzone
        accept="application/pdf,.pdf"
        multiple={false}
        disabled={loading || thumbLoading}
        acceptText="Dépose ton PDF ici"
        helperText="Glisse-dépose un PDF ou clique sur “Choisir un fichier”"
        onFiles={(files) => onPick(files?.[0] || null)}
      />

      {file && (
        <p style={{ textAlign: "center", color: "#666", marginTop: 8 }}>
          Fichier : <b>{file.name}</b>{" "}
          {typeof pageCount === "number" && (
            <>
              — Pages : <b>{pageCount}</b> — Sélection :{" "}
              <b>{selectedList.length}</b>
            </>
          )}
        </p>
      )}

      {/* Controls */}
      <div style={{ maxWidth: 780, margin: "12px auto", textAlign: "left" }}>
        <div
          style={{
            display: "flex",
            gap: 10,
            flexWrap: "wrap",
            marginBottom: 10,
          }}
        >
          <button
            type="button"
            onClick={selectAll}
            disabled={!file || loading || thumbLoading}
          >
            Tout sélectionner
          </button>
          <button
            type="button"
            onClick={selectNone}
            disabled={!file || loading || thumbLoading}
          >
            Tout désélectionner
          </button>
        </div>

        <label style={{ display: "block", marginBottom: 10 }}>
          Mode de sortie
          <select
            value={mode}
            onChange={(e) => setMode(e.target.value)}
            disabled={!file || loading || thumbLoading}
            style={{
              width: "100%",
              padding: 10,
              marginTop: 6,
              borderRadius: 10,
              border: "1px solid #ccc",
            }}
          >
            <option value="single">Un PDF</option>
            <option value="multi">Le PDF en (ZIP)</option>
          </select>
        </label>
      </div>

      {/* Thumbnails */}
      {thumbLoading && (
        <p style={{ textAlign: "center", color: "#666", marginTop: 10 }}>
          Génération des miniatures…
        </p>
      )}

      {thumbs.length > 0 && typeof pageCount === "number" && (
        <div
          style={{
            maxWidth: 980,
            margin: "10px auto 0",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
            gap: 12,
          }}
        >
          {thumbs.map((t) => {
            const isOn = selected.has(t.pageNum);
            return (
              <button
                key={t.pageNum}
                type="button"
                onClick={() => togglePage(t.pageNum)}
                disabled={loading || thumbLoading}
                style={{
                  border: isOn ? "2px solid #4caf50" : "1px solid #e6e6e6",
                  background: isOn ? "#f2fff3" : "#fff",
                  borderRadius: 12,
                  padding: 10,
                  cursor: "pointer",
                  textAlign: "left",
                }}
                title="Cliquer pour sélectionner/désélectionner"
              >
                <img
                  src={t.url}
                  alt={`Page ${t.pageNum}`}
                  style={{
                    width: "100%",
                    height: 150,
                    objectFit: "contain",
                    background: "#f4f4f4",
                    borderRadius: 10,
                    display: "block",
                  }}
                />
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginTop: 8,
                    alignItems: "center",
                  }}
                >
                  <div style={{ fontWeight: 800, color: "#222" }}>
                    Page {t.pageNum}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 800,
                      color: isOn ? "#2e7d32" : "#777",
                    }}
                  >
                    {isOn ? "Sélectionnée" : "—"}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Actions */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: 12,
          flexWrap: "wrap",
          marginTop: 16,
        }}
      >
        <button onClick={runSplit} disabled={!canRun} style={{ minWidth: 220 }}>
          {loading ? "Extraction..." : "Extraire"}
        </button>

        {file && (
          <button
            type="button"
            onClick={resetAll}
            disabled={loading || thumbLoading}
            style={{ minWidth: 160 }}
          >
            Réinitialiser
          </button>
        )}
      </div>

      {/* Download */}
      <div style={{ textAlign: "center", marginTop: 16 }}>
        {result?.url && (
          <a href={result.url} download={result.name}>
            Télécharger le PDF extrait
          </a>
        )}

        {zipUrl && mode === "multi" && (
          <a
            href={zipUrl}
            download={`${baseName(file?.name || "document")}-extraits.zip`}
          >
            Télécharger le ZIP (PDF par page)
          </a>
        )}
      </div>
    </section>
  );
}
